/**
 * Unit catalog — the curated "what are you limiting?" choices for authoring a consequence policy, and
 * the helpers that turn a choice into the dimension's storage key + DimMeta. The engine is unit-agnostic
 * (it seeds no unit); this is a FRONTEND authoring convenience so an operator picks a currency or a
 * count instead of hand-typing a dimension key + exponent (the raw-cents / wrong-exponent footgun).
 *
 * A currency's precision is derived AUTHORITATIVELY from Intl (ISO 4217: USD/EUR=2, JPY/KRW=0,
 * BHD/KWD=3) — never hand-typed, so a zero-decimal currency can't be mistaken for a 2-decimal one. A
 * currency's storage key is its lowercase ISO code (so the existing "usd" dimension stays compatible).
 */

import type { DimMeta } from "./consequence-format";

/** A curated common currency (ISO 4217 alphabetic code). The picker also allows any other code. */
export const COMMON_CURRENCIES: readonly string[] = [
  "USD", "EUR", "GBP", "JPY", "CHF", "CAD", "AUD", "CNY", "INR", "BRL",
  "MXN", "SGD", "HKD", "SEK", "NOK", "ZAR", "AED", "SAR", "KRW", "BHD", "KWD",
];

/** A curated common non-money (count) unit: a stable lowercase key + a human label. */
export interface CountUnit {
  key: string;
  label: string;
}

export const COMMON_COUNT_UNITS: readonly CountUnit[] = [
  { key: "records", label: "Records" },
  { key: "api_calls", label: "API calls" },
  { key: "requests", label: "Requests" },
  { key: "tokens", label: "Tokens" },
  { key: "messages", label: "Messages" },
  { key: "emails", label: "Emails" },
  { key: "alerts", label: "Alerts" },
  { key: "rows", label: "Rows" },
  { key: "files", label: "Files" },
  { key: "gb", label: "GB" },
  { key: "seats", label: "Seats" },
  { key: "transactions", label: "Transactions" },
];

/**
 * currencyExponent is the currency's minor-unit exponent, per ISO 4217, resolved from Intl (the
 * authoritative source — not a hand-maintained table). Falls back to 2 only if the runtime can't
 * format the code (an exotic/unknown code); the caller should treat that as a hint to double-check.
 */
export function currencyExponent(code: string): number {
  try {
    const digits = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code,
    }).resolvedOptions().maximumFractionDigits;
    return typeof digits === "number" ? digits : 2;
  } catch {
    return 2;
  }
}

/** currencyName is the localized display name for a currency code ("US Dollar", "Euro"), or the code. */
export function currencyName(code: string): string {
  try {
    return new Intl.DisplayNames(undefined, { type: "currency" }).of(code.toUpperCase()) ?? code.toUpperCase();
  } catch {
    return code.toUpperCase();
  }
}

/** currencyKey is the storage dimension key for a currency: the lowercase ISO code (keeps "usd" stable). */
export function currencyKey(code: string): string {
  return code.toLowerCase();
}

/** currencyDimMeta builds the DimMeta for a currency code — name from Intl, exponent from ISO 4217. */
export function currencyDimMeta(code: string): DimMeta {
  const upper = code.toUpperCase();
  return { displayName: currencyName(upper), exponent: currencyExponent(upper), unitCode: upper };
}

/** isLikelyCurrencyCode is a cheap shape check for the "any ISO code" escape (3 letters). */
export function isLikelyCurrencyCode(s: string): boolean {
  return /^[A-Za-z]{3}$/.test(s.trim());
}

// The set of REAL ISO 4217 currency codes the runtime recognizes, from Intl.supportedValuesOf (the
// authoritative active-currency list — ~160 codes). Computed once. This is deliberately distinct from
// the SHAPE check above: Intl.NumberFormat does NOT validate a currency code — it happily formats an
// unknown 3-letter key ("GPU", "POD", "KWH") as a 2-decimal currency — so shape alone must never decide
// money-vs-count, or a whole-count dimension gets silently scaled 100×. When a runtime lacks
// supportedValuesOf we fall back to the curated COMMON_CURRENCIES so a real currency still formats, and
// everything else degrades to a whole-count (factor 1) — never a false ×100.
const knownCurrencyCodes: ReadonlySet<string> = (() => {
  try {
    const supportedValuesOf = (Intl as { supportedValuesOf?: (key: string) => string[] })
      .supportedValuesOf;
    if (typeof supportedValuesOf === "function") {
      return new Set(supportedValuesOf("currency").map((c) => c.toUpperCase()));
    }
  } catch {
    // fall through to the curated set
  }
  return new Set(COMMON_CURRENCIES);
})();

/**
 * isKnownCurrencyCode — true ONLY for a real ISO 4217 currency the runtime recognizes (USD, EUR, JPY,
 * BHD, …), never merely a 3-letter shape. Use THIS (not isLikelyCurrencyCode) to decide whether a
 * dimension key is money: a 3-letter non-currency key (gpu, pod, kwh, api, job) is a COUNT, and
 * rendering/scaling it as a currency would misstate its magnitude by the currency's exponent (usually
 * ×100). Codes are compared upper-cased.
 */
export function isKnownCurrencyCode(code: string): boolean {
  return knownCurrencyCodes.has(code.trim().toUpperCase());
}

/**
 * slugifyDimensionKey turns a free-text custom-unit label into a valid dimension key — lowercase,
 * [a–z0–9_], leading letter, ≤64 chars (matching the backend dimensionKeyRe + DB CHECK). Returns "" if
 * nothing valid can be derived (the caller must block a save on an empty key).
 */
export function slugifyDimensionKey(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/^[^a-z]+/, "")
    .slice(0, 64);
}
